import React, { useState, useMemo } from 'react';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Wrench,
  Gauge,
  PlusCircle,
  RefreshCw,
  Bell,
  ShieldCheck,
} from 'lucide-react';
import type { Machine } from '../types';
import {
  generateFleetReminders,
  evaluateMachine,
  formatHours,
  type ReminderSummary,
} from '../services/scheduler';
import {
  ReminderBanner,
  MachineList,
  StatusBadge,
} from '../components/dashboard';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { updateMachineHours } from '../db';

export interface DashboardViewProps {
  machines: Machine[];
  selectedMachineId?: string;
  onSelectMachine?: (machineId: string) => void;
  onStartInspection: (machineId: string) => void;
  onRefresh?: () => Promise<void> | void;
  className?: string;
}

export function DashboardView({
  machines,
  selectedMachineId,
  onSelectMachine,
  onStartInspection,
  onRefresh,
  className = '',
}: DashboardViewProps) {
  // Modal state for adding operating hours
  const [hoursModalMachineId, setHoursModalMachineId] = useState<string | null>(null);
  const [hoursToAdd, setHoursToAdd] = useState<string>('8');
  const [isUpdatingHours, setIsUpdatingHours] = useState<boolean>(false);
  const [hoursError, setHoursError] = useState<string>('');

  // Compute fleet reminders and evaluation metrics
  const remindersSummary: ReminderSummary = useMemo(() => {
    return generateFleetReminders(machines);
  }, [machines]);

  // Aggregate statistics
  const metrics = useMemo(() => {
    let normalCount = 0;
    let dueCount = 0;
    let overdueCount = 0;
    let totalHours = 0;

    for (const machine of machines) {
      const evaluation = evaluateMachine(machine);
      const st = evaluation.overallStatus;
      if (st === 'OVERDUE') overdueCount++;
      else if (st === 'DUE_SOON') dueCount++;
      else normalCount++;

      totalHours += machine.cumulativeHours || 0;
    }

    return {
      total: machines.length,
      normal: normalCount,
      due: dueCount,
      overdue: overdueCount,
      totalHours,
      avgHours: machines.length > 0 ? Math.round(totalHours / machines.length) : 0,
    };
  }, [machines]);

  // Handle open add hours modal
  const handleOpenAddHours = (machineId: string, currentHours: number) => {
    setHoursModalMachineId(machineId);
    setHoursToAdd('8');
    setHoursError('');
  };

  // Handle save hours
  const handleSaveHours = async () => {
    if (!hoursModalMachineId) return;
    const addNum = parseFloat(hoursToAdd);
    if (isNaN(addNum) || addNum <= 0) {
      setHoursError('กรุณาระบุจำนวนชั่วโมงที่ถูกต้อง (มากกว่า 0)');
      return;
    }

    const targetMachine = machines.find((m) => m.id === hoursModalMachineId);
    if (!targetMachine) return;

    try {
      setIsUpdatingHours(true);
      const newTotal = (targetMachine.cumulativeHours || 0) + addNum;
      await updateMachineHours(targetMachine.id, newTotal);
      if (onRefresh) {
        await onRefresh();
      }
      setHoursModalMachineId(null);
    } catch (err) {
      console.error('[DashboardView] Error updating hours:', err);
      setHoursError('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setIsUpdatingHours(false);
    }
  };

  const selectedMachineForHours = machines.find((m) => m.id === hoursModalMachineId);

  return (
    <div className={`space-y-6 animate-in fade-in duration-150 ${className}`}>
      {/* 1. In-App / PWA Reminder Banner */}
      <ReminderBanner
        summary={remindersSummary}
        onSelectMachine={onSelectMachine}
        onStartInspection={onStartInspection}
      />

      {/* 2. Top Summary KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {/* Card 1: Normal / Up-to-Date */}
        <div className="bg-white border border-emerald-200 rounded-2xl p-4 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider">
              ปกติ (Up-to-Date)
            </span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-emerald-600 font-mono">
              {metrics.normal}
            </span>
            <span className="text-xs text-slate-500 font-medium">/ {metrics.total} เครื่อง</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            พร้อมปฏิบัติงานตามมาตรฐาน
          </p>
        </div>

        {/* Card 2: Due Soon */}
        <div className="bg-white border border-amber-200 rounded-2xl p-4 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">
              ใกล้ถึงกำหนด (Due Soon)
            </span>
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-amber-600 font-mono">
              {metrics.due}
            </span>
            <span className="text-xs text-slate-500 font-medium">เครื่อง</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            เข้าสู่รอบเตือนล่วงหน้า (90%)
          </p>
        </div>

        {/* Card 3: Overdue */}
        <div className="bg-white border border-rose-200 rounded-2xl p-4 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-rose-700 uppercase tracking-wider">
              เกินกำหนด (Overdue)
            </span>
            <div className="p-2 rounded-lg bg-rose-50 text-rose-600">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-rose-600 font-mono">
              {metrics.overdue}
            </span>
            <span className="text-xs text-slate-500 font-medium">เครื่อง</span>
          </div>
          <p className="text-[11px] text-rose-700 font-semibold mt-1">
            ต้องตรวจเช็กทันที
          </p>
        </div>

        {/* Card 4: Operating Hours Total */}
        <div className="bg-white border border-blue-200 rounded-2xl p-4 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
              ชั่วโมงสะสมรวม
            </span>
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <Gauge className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="text-2xl sm:text-3xl font-black text-blue-700 font-mono">
              {formatHours(metrics.totalHours, false)}
            </span>
            <span className="text-xs text-slate-500 font-bold">ชม.</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            เฉลี่ย {formatHours(metrics.avgHours)} / เครื่อง
          </p>
        </div>
      </div>

      {/* 3. CNC Machine Fleet Management Header */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <div>
          <h2 className="text-lg sm:text-xl font-black text-slate-900 flex items-center gap-2 tracking-wide">
            <Wrench className="w-5 h-5 text-blue-600" />
            รายการเครื่องจักร & สถานะบำรุงรักษา (CNC Fleet Status)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            ติดตามสถานะแบบเรียลไทม์ตามรอบความถี่และชั่วโมงทำงาน
          </p>
        </div>

        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="text-xs bg-white hover:bg-slate-100 text-slate-700 px-3 py-2 rounded-xl border border-slate-300 font-medium transition-colors flex items-center gap-1.5 min-h-[40px] shadow-2xs"
            title="รีเฟรชข้อมูล"
          >
            <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
            <span className="hidden sm:inline">รีเฟรช</span>
          </button>
        )}
      </div>

      {/* 4. Machine Fleet List with Filter, Search & Hours Tracking */}
      <MachineList
        machines={machines}
        selectedMachineId={selectedMachineId}
        onSelectMachine={onSelectMachine}
        onStartInspection={onStartInspection}
        onAddHours={handleOpenAddHours}
      />

      {/* 5. Add Running Hours Quick Modal */}
      <Modal
        isOpen={Boolean(hoursModalMachineId)}
        onClose={() => setHoursModalMachineId(null)}
        title={
          <div className="flex items-center gap-2 text-slate-900">
            <Gauge className="w-5 h-5 text-blue-600" />
            <span>บันทึกชั่วโมงทำงานเครื่องจักร</span>
          </div>
        }
        maxWidth="md"
        footer={
          <div className="flex items-center justify-end gap-2 w-full">
            <Button
              variant="secondary"
              onClick={() => setHoursModalMachineId(null)}
              disabled={isUpdatingHours}
            >
              ยกเลิก
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveHours}
              disabled={isUpdatingHours}
              leftIcon={<PlusCircle className="w-4 h-4" />}
            >
              {isUpdatingHours ? 'กำลังบันทึก...' : 'บันทึกชั่วโมง'}
            </Button>
          </div>
        }
      >
        {selectedMachineForHours && (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-sm font-bold text-slate-900">
                {selectedMachineForHours.name}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                รหัส: {selectedMachineForHours.id} | รุ่น: {selectedMachineForHours.model}
              </p>
              <p className="text-xs text-slate-700 mt-2 font-mono">
                ชั่วโมงทำงานปัจจุบัน:{' '}
                <strong className="text-blue-700">
                  {formatHours(selectedMachineForHours.cumulativeHours)}
                </strong>
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                จำนวนชั่วโมงทำงานที่ต้องการเพิ่ม (ชม.):
              </label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={hoursToAdd}
                onChange={(e) => setHoursToAdd(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-lg font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[48px] shadow-xs"
                placeholder="8"
              />
              <div className="flex items-center gap-2 mt-2">
                {[4, 8, 12, 16, 24].map((hrs) => (
                  <button
                    key={hrs}
                    type="button"
                    onClick={() => setHoursToAdd(hrs.toString())}
                    className="text-xs bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-700 px-2.5 py-1 rounded-lg border border-slate-300 font-mono transition-colors shadow-2xs font-semibold"
                  >
                    +{hrs}h
                  </button>
                ))}
              </div>
              {hoursError && (
                <p className="text-xs font-bold text-rose-600 mt-2">{hoursError}</p>
              )}
            </div>

            <p className="text-xs text-slate-500">
              การเพิ่มชั่วโมงทำงานจะส่งผลต่อการคำนวณรอบบำรุงรักษา 500h, 1,000h, 2,000h,
              8,000h, 10,000h โดยอัตโนมัติ
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
